import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Avatar,
  Stack,
  Divider,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper
} from '@mui/material';
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Search as SearchIcon
} from '@mui/icons-material';

interface Message {
  id: string;
  sender: string;
  senderAvatar: string;
  content: string;
  timestamp: string;
  isFromClient: boolean;
  projectId?: string;
  projectName?: string;
}

/**
 * Messages Page
 * Client messaging interface with project team
 */
const Messages: React.FC = () => {
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Mock messages data
  const [messages] = useState<Message[]>([
    {
      id: '1',
      sender: 'Sarah Johnson',
      senderAvatar: '/avatars/sarah.jpg',
      content: 'Hi! We\'ve completed the initial wireframes for your project. Please review and let us know your thoughts.',
      timestamp: '2025-10-23T10:30:00Z',
      isFromClient: false,
      projectId: '1',
      projectName: 'E-commerce Website'
    },
    {
      id: '2',
      sender: 'You',
      senderAvatar: '/avatars/client.jpg',
      content: 'Thanks Sarah! The wireframes look great. I have a few questions about the checkout process.',
      timestamp: '2025-10-23T11:15:00Z',
      isFromClient: true,
      projectId: '1',
      projectName: 'E-commerce Website'
    },
    {
      id: '3',
      sender: 'Mike Chen',
      senderAvatar: '/avatars/mike.jpg',
      content: 'The development environment is ready. We can start the coding phase next week.',
      timestamp: '2025-10-23T14:20:00Z',
      isFromClient: false,
      projectId: '1',
      projectName: 'E-commerce Website'
    }
  ]);

  const handleSendMessage = () => {
    if (newMessage.trim()) {
      // In a real app, this would send the message to the backend
      console.log('Sending message:', newMessage);
      setNewMessage('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const filteredMessages = messages.filter(message =>
    message.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
    message.sender.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (message.projectName && message.projectName.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <Box sx={{ height: '600px', display: 'flex', flexDirection: 'column' }}>
      {/* Search */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Search messages..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
          }}
        />
      </Box>

      {/* Messages List */}
      <Paper 
        sx={{ 
          flex: 1, 
          overflow: 'auto', 
          p: 2, 
          bgcolor: 'grey.50',
          mb: 2
        }}
      >
        {filteredMessages.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              {searchTerm ? 'No messages found matching your search.' : 'No messages yet. Start a conversation!'}
            </Typography>
          </Box>
        ) : (
          <Stack spacing={2}>
            {filteredMessages.map((message) => (
              <Box
                key={message.id}
                sx={{
                  display: 'flex',
                  justifyContent: message.isFromClient ? 'flex-end' : 'flex-start',
                }}
              >
                <Box
                  sx={{
                    maxWidth: '70%',
                    display: 'flex',
                    flexDirection: message.isFromClient ? 'row-reverse' : 'row',
                    alignItems: 'flex-end',
                    gap: 1
                  }}
                >
                  <Avatar 
                    sx={{ width: 32, height: 32 }}
                    src={message.senderAvatar}
                  >
                    {message.sender.charAt(0)}
                  </Avatar>
                  
                  <Card
                    sx={{
                      bgcolor: message.isFromClient ? 'primary.main' : 'white',
                      color: message.isFromClient ? 'white' : 'text.primary'
                    }}
                  >
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Stack spacing={1}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="caption" sx={{ opacity: 0.8 }}>
                            {message.sender}
                          </Typography>
                          {message.projectName && (
                            <Chip 
                              label={message.projectName} 
                              size="small" 
                              variant="outlined"
                              sx={{ 
                                color: message.isFromClient ? 'white' : 'primary.main',
                                borderColor: message.isFromClient ? 'white' : 'primary.main'
                              }}
                            />
                          )}
                        </Stack>
                        
                        <Typography variant="body2">
                          {message.content}
                        </Typography>
                        
                        <Typography variant="caption" sx={{ opacity: 0.6 }}>
                          {new Date(message.timestamp).toLocaleString()}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                </Box>
              </Box>
            ))}
          </Stack>
        )}
      </Paper>

      {/* Message Input */}
      <Card>
        <CardContent sx={{ p: 2 }}>
          <Stack direction="row" spacing={1} alignItems="flex-end">
            <TextField
              fullWidth
              multiline
              maxRows={4}
              placeholder="Type your message..."
              variant="outlined"
              size="small"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
            />
            <IconButton color="primary">
              <AttachFileIcon />
            </IconButton>
            <Button
              variant="contained"
              endIcon={<SendIcon />}
              onClick={handleSendMessage}
              disabled={!newMessage.trim()}
            >
              Send
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Messages;